import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from 'api';
import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import BulkUploadsPolling from 'components/BulkUploadsPolling';
import { routePaths } from 'components/Routes/data';
import {
  BulkUploadStatus,
  BulkUploadsExecutionPollStatuses,
  uploadTypeConfigMap,
} from 'constants/bulkUpload';
import { BulkUploadDetailsSchemaContract } from 'schemas/BulkUploadDetailsSchema';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import { BulkUploadSummary } from '../BulkUploads/BladeComponents/BulkUploadSummary';
import { PreviewTable } from '../BulkUploads/BladeComponents/PreviewTable';
import { GET_BULK_UPLOAD_RECORD } from './queries/queries';
import { BulkUploadPreviewModals } from './types';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
} from 'components/WizardViews/components';
import {
  Box,
  Button,
  Heading,
  Spinner,
  TabItem,
  TabList,
  Tabs,
  Text,
  useToast,
} from '@razorpay/blade/components';
import { ColumnsSelector } from './BladeComponents/ColumnsSelector';
import { useColumnsAndTabsSelectorData } from './hooks/useColumnsAndTabsData';
import { ConfirmExecutionModal } from './BladeComponents/ExecutionModals/ConfirmExecutionModal';
import { useBladePreviewModals } from './hooks/useBladePreviewModals';
import { useBulkUploadType } from './hooks/useBulkUploadType';
import { useBulkUploadTypeConfig } from './hooks/useBulkUploadTypeConfig';
import FilterTag from 'components/ui/FilterTag/FilterTag';

const BulkUploadsPreview = () => {
  const bulkUploadType = useBulkUploadType();
  const pollingRequestId = `preview_${bulkUploadType}_polling_request_id`;
  const uploadTypeConfig = useBulkUploadTypeConfig(bulkUploadType);
  const renderConfirmationText = uploadTypeConfig.preview.renderConfirmationText;
  const onSucessClick = uploadTypeConfig.preview.onSuccessModalCtaClick;
  const [showConfirmModal, setConfirmModal] = useState(false);
  const previewConfig = uploadTypeConfig.preview;
  const toast = useToast();

  const {
    tabsData: { showTabs, tabs, value: tabValue, onTabChange },
    columnsData: { renderColumns, ...columnSelectorProps },
    sheetNumber,
  } = useColumnsAndTabsSelectorData({ bulkUploadType });

  const history = useHistory();
  const onBackClick = useCallback(() => {
    history.push({
      pathname: routePaths.bulkUploads.root,
      search: `?type=${bulkUploadType}`,
    });
  }, [bulkUploadType]);

  const [bulkUploadRecord, setBulkUploadRecord] = useState<
    BulkUploadDetailsSchemaContract[0] | null
  >(null);
  const [modalToDisplay, setModalToDisplay] = useState<BulkUploadPreviewModals>(
    BulkUploadPreviewModals.SHOW_NO_MODALS,
  );
  const callPauseRef = useRef(true);

  const { isLoading: isLoadingBulkUploadRecord, isFetching: isFetchingBulkUploadRecord } = useQuery(
    GET_BULK_UPLOAD_RECORD,
    () => {
      return api.bulkUploads.fetchBulkUploadRecordByType(bulkUploadType);
    },
    {
      onSuccess: (data) => {
        setBulkUploadRecord(data.length > 0 ? data[0] : null);
        if (localStorage.getItem(pollingRequestId) === null) {
          const temp = data ? data[0].id : null;
          if (temp) {
            localStorage.setItem(pollingRequestId, temp.toString());
          } else {
            onBackClick?.();
          }
        }
      },
      onError: (error: typeof AppError) => {
        window.scrollTo(0, 0);
        const response = getAppErrorDetails(error);
        if (response.type === 403) {
          history.replace(routePaths.accessDenied);
        }
      },
    },
  );

  const { mutate: executeBulkUpload, isLoading: isExecutionInProgress } = useMutation({
    mutationFn: () => {
      if (bulkUploadRecord) {
        return api.bulkUploads.sendExecuteRequest(bulkUploadRecord.id);
      } else {
        return Promise.reject(new Error('Bulk upload record not found'));
      }
    },
    onSuccess: (response) => {
      setBulkUploadRecord(response[0] ?? bulkUploadRecord);
      sessionStorage.setItem('bulkUploadPollId', String(response[0].id));
      setConfirmModal(false);
    },
    onError: (error: typeof AppError) => {
      toast.show({
        color: 'negative',
        content: 'Something went wrong, please try again later',
      });
    },
  });

  const onConfirmExecutionClick = () => {
    if (bulkUploadRecord) {
      sessionStorage.removeItem('bulkUploadPollId');
      executeBulkUpload();
    }
  };

  const onCancelExecutionClick = () => {
    if (isExecutionInProgress) {
      return;
    }
    setConfirmModal(false);
  };

  const hideModal = () => {
    setModalToDisplay(BulkUploadPreviewModals.SHOW_NO_MODALS);
  };

  useEffect(() => {
    switch (bulkUploadRecord?.status) {
      case BulkUploadStatus.STATUS_VALIDATION_PENDING:
      case BulkUploadStatus.STATUS_VALIDATION_PROGRESS:
      case BulkUploadStatus.STATUS_VALIDATION_FAILED:
        onBackClick?.();
        break;
      case BulkUploadStatus.STATUS_EXECUTION_PENDING:
      case BulkUploadStatus.STATUS_EXECUTION_PROGRESS:
        setModalToDisplay(BulkUploadPreviewModals.SHOW_IN_PROGRESS_MODAL);
        break;
      case BulkUploadStatus.STATUS_EXECUTION_PARTIAL_SUCCESS:
      case BulkUploadStatus.STATUS_EXECUTION_FAILED:
        setModalToDisplay(BulkUploadPreviewModals.SHOW_PARTIAL_UPLOAD_MODAL);
        break;
      case BulkUploadStatus.STATUS_EXECUTION_SUCCESS:
        (() => {
          if (callPauseRef.current) {
            callPauseRef.current = false;

            setModalToDisplay(BulkUploadPreviewModals.SHOW_SUCCESS_MODAL);
          }
        })();
        break;
    }
  }, [bulkUploadRecord, onBackClick]);

  const shouldDisplayExecutionPolling =
    bulkUploadRecord?.id &&
    BulkUploadsExecutionPollStatuses.includes(
      bulkUploadRecord?.status ?? BulkUploadStatus.STATUS_VALIDATION_SUCCESS,
    );

  const tableTopView = previewConfig.tableTopView;
  const successModalCtaText = previewConfig.successModalCtaText;

  const previewModalToShow = useBladePreviewModals({
    bulkUploadRecord: bulkUploadRecord ?? ({} as BulkUploadDetailsSchemaContract[0]),
    modalToDisplay,
    labelMap: {
      success: {
        buttonText: successModalCtaText as string,
        mainText: previewConfig.successModalMainText,
        subText: previewConfig.successModalSubText,
      },
    },
    clickHandlers: {
      onClickGetErrorReport() {
        hideModal();
        previewConfig.onDownloadErrorReport?.(history);
      },
      onClickGoBack: hideModal,
      onClickSuccessCallback() {
        onSucessClick?.();
      },
    },
  });

  const executionConfirmationText = bulkUploadRecord
    ? renderConfirmationText?.(bulkUploadRecord)
    : '';

  return (
    <>
      <FormWrapper>
        <>
          {bulkUploadRecord ? previewModalToShow : null}

          {shouldDisplayExecutionPolling ? (
            <BulkUploadsPolling
              id={bulkUploadRecord?.id.toString()}
              action={'file-execution'}
              setBulkUploadRecordData={setBulkUploadRecord}
            />
          ) : null}

          <StepContentWrapper noBgIllustration maxWidth="100%" width="100%">
            <Box>
              {Boolean(bulkUploadRecord) && (
                <BulkUploadSummary bulkUploadRecord={bulkUploadRecord} isInputDataHidden />
              )}
              {Boolean(showTabs && bulkUploadRecord) && (
                <Box marginTop="spacing.6">
                  <Tabs value={tabValue}>
                    <TabList>
                      {tabs.map(({ title, value }) => {
                        return (
                          <TabItem
                            value={value}
                            onClick={() => {
                              onTabChange(value);
                            }}>
                            {title}
                          </TabItem>
                        );
                      })}
                    </TabList>
                  </Tabs>
                </Box>
              )}
              <Box
                marginTop="spacing.6"
                display="flex"
                justifyContent="space-between"
                alignItems="center">
                <Box display="flex" flexDirection="column" justifyContent="center">
                  <Heading marginBottom="spacing.2" size="medium" color="surface.text.gray.subtle">
                    Preview Upload
                  </Heading>
                  <Text color="surface.text.gray.muted">
                    This is how we are processing your data
                  </Text>
                </Box>
                {showTabs ? <ColumnsSelector {...columnSelectorProps} /> : null}
              </Box>
              {showTabs && columnSelectorProps.values.length ? (
                <Box display='flex' marginTop='spacing.6'>
                  <FilterTag
                    name="Filter"
                    value={columnSelectorProps.values}
                    onDismiss={() => {
                      columnSelectorProps.onChange([]);
                    }}
                  />
                </Box>
              ) : null}
              {isFetchingBulkUploadRecord || isLoadingBulkUploadRecord ? (
                <Box
                  width="100%"
                  height="100%"
                  display="flex"
                  justifyContent="center"
                  alignContent="center">
                  <Spinner accessibilityLabel="loading details" />
                </Box>
              ) : (
                <PreviewTable
                  tableTopView={tableTopView}
                  bulkUploadRecord={bulkUploadRecord}
                  columns={renderColumns}
                  sheetNumber={sheetNumber}
                  activeTabValue={tabValue}
                />
              )}
            </Box>
          </StepContentWrapper>

          <StepActionsWrapper paddingX="spacing.8" maxWidth="100%" width="100%">
            <Button
              variant="secondary"
              onClick={onBackClick}
              isDisabled={isFetchingBulkUploadRecord || isLoadingBulkUploadRecord}>
              Back
            </Button>
            <Button
              onClick={() => setConfirmModal(true)}
              isDisabled={isFetchingBulkUploadRecord || isLoadingBulkUploadRecord}
              isLoading={isExecutionInProgress}>
              Confirm Upload
            </Button>
          </StepActionsWrapper>
        </>
      </FormWrapper>
      <ConfirmExecutionModal
        description={executionConfirmationText}
        isOpen={showConfirmModal}
        onDismiss={onCancelExecutionClick}
        onConfirm={onConfirmExecutionClick}
        isExecutionInProgress={isExecutionInProgress}
      />
    </>
  );
};
export default BulkUploadsPreview;
